// A Provider to allow the components in AddAircraftModal to determine if the user can add an aircraft or asset to their account.

import {
  createContext, useContext, useMemo, useState, useCallback,
} from 'react';

export const ADD_TO_ACCOUNT_FLAGS = {
  REGISTERED_BY_THIS_COMPANY: 'registeredByThisCompany',
  REGISTERED_BY_OTHER_COMPANY: 'registeredByOtherCompany',
  FORM_INCOMPLETE: 'formIncomplete',
};

const defaultFlags = {
  registeredByThisCompany: false,
  registeredByOtherCompany: false,
  formIncomplete: true,
};

const CanAddToAccountContext = createContext();

export function CanAddToAccountProvider({ children }) {
  const [canAddToAccount, setCanAddToAccount] = useState(defaultFlags);

  const updateCanAddToAccount = useCallback((updates) => {
    setCanAddToAccount((prev) => ({ ...prev, ...updates }));
  }, []);

  // Computed properties for common checks
  const canSubmit = useMemo(() => {
    // Cannot submit if form is incomplete or has validation errors
    if (canAddToAccount.formIncomplete || canAddToAccount.hasValidationError) {
      return false;
    }

    // Cannot submit if already registered by this company and NOT deactive on this company
    if (canAddToAccount.registeredByThisCompany && !canAddToAccount.existsDeactive) {
      return false;
    }

    return true;
  }, [canAddToAccount]);

  const showExistingAircraftNotice = useMemo(() => (
    !canAddToAccount.registeredByThisCompany && (canAddToAccount.registeredByOtherCompany)
  ), [canAddToAccount.registeredByThisCompany, canAddToAccount.registeredByOtherCompany]);

  const providedValue = useMemo(() => ({
    canAddToAccount,
    setCanAddToAccount,
    updateCanAddToAccount,
    canSubmit,
    showExistingAircraftNotice,
  }), [canAddToAccount, canSubmit, showExistingAircraftNotice, updateCanAddToAccount]);

  return (
    <CanAddToAccountContext.Provider value={providedValue}>
      {children}
    </CanAddToAccountContext.Provider>
  );
}

export const useCanAddToAccount = () => useContext(CanAddToAccountContext);
